import Actionbar from "src/components/common/actionbar/Authactionbar";
import { useEffect, useCallback, useMemo } from "react";
import { Message } from "src/components/common/form/Message";
import useUiuxStore from "src/store/useUiuxStore";
import { SearchSection_EQM1030_R01 } from "./SearchSection_EQM1030_R01";
import { Table_EQM1030_R01 } from "./Table_EQM1030_R01";
import { Chart_EQM1030_R01 } from "./Chart_EQM1030_R01";
import { useEQM1030_R01Store, TabType } from "./Store_EQM1030_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { getEQM1030_R01List, exportEQM1030_R01Excel, getEQM1030_R01ChartData } from "./Api_EQM1030_R01";
import { downloadExcel } from "src/utils/blob/blobUtil";

export const Container_EQM1030_R01 = () => {
    const { initData, activeTab, setActiveTab } = useEQM1030_R01Store();
    const { setMessage } = useUiuxStore();
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    const {
        EQM1030_R01Filter,
        EQM1030_R01PastFilter,
        setEQM1030_R01Filter,
        setEQM1030_R01PastFilter,
        EQM1030_R01List,
        setEQM1030_R01List,
        chartData,
        setChartData,
        setLatestUpdateTime,
    } = useEQM1030_R01Store();

    useFetchActionbarAuth("EQM1030_R01");

    const isSameDate = (a?: Date, b?: Date) => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    };
    const isSameFilter = (a: typeof EQM1030_R01Filter, b: typeof EQM1030_R01Filter) => 
        a.compCd === b.compCd && 
        isSameDate(a.baseDate, b.baseDate);

    const fetchData = async () => {
        setLoadingSpinner(true);
        try {
            if (activeTab === 'table') {
                // Table 데이터 조회
                const same = isSameFilter(EQM1030_R01Filter, EQM1030_R01PastFilter);
                const nextListLength = same ? EQM1030_R01List.length : 0;
                if (!same && EQM1030_R01List.length > 0) setEQM1030_R01List([]);
                // 사용자가 선택한 baseDate로 조회
                const response = await getEQM1030_R01List('', EQM1030_R01Filter, nextListLength);
                const raw = response.data.data || [];
                if (raw.length === 0) {
                    showWarning('조회된 데이터가 없습니다.');
                    setLoadingSpinner(false);
                    return;
                }
                const newList = same ? [...EQM1030_R01List, ...raw] : [...raw];
                setEQM1030_R01List(newList);
                setEQM1030_R01PastFilter(EQM1030_R01Filter);
                // createdatetime(또는 insertDt/updateDt) '이번 응답(raw)'의 최대값을 검색 섹션의 현재시간으로 표시
                // 누적 리스트(newList)가 아니라, 매 요청 응답(raw)에서만 계산합니다.
                // 항상 최신 시간을 latestUpdateTime에 업데이트 (현재시간 표시용)
                try {
                    const toTime = (v: any): number => {
                        if (!v) return 0;
                        const d = typeof v === 'string' || typeof v === 'number' ? new Date(v) : v;
                        const t = d instanceof Date && !isNaN(d.getTime()) ? d.getTime() : 0;
                        return t;
                    };
                    let maxTs = 0;
                    for (const item of raw as any[]) {
                        const cand = Math.max(
                            toTime((item as any).latestTime),
                            toTime((item as any).createdatetime),
                            toTime(item.insertDt),
                            toTime(item.updateDt)
                        );
                        if (cand > maxTs) maxTs = cand;
                    }
                    if (maxTs > 0) {
                        setLatestUpdateTime(new Date(maxTs));
                    } else {
                        setLatestUpdateTime(new Date());
                    }
                } catch {}

                ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}건)`);
            } else if (activeTab === 'chart') {
                // Chart 데이터 조회
                const chartResponse = await getEQM1030_R01ChartData(EQM1030_R01Filter);
                const chartRaw = chartResponse.data.data || [];
                if (chartRaw.length === 0) {
                    showWarning('조회된 차트 데이터가 없습니다.');
                    setLoadingSpinner(false);
                    return;
                }
                setChartData(chartRaw);
                ShowSuccessFetch(setMessage, '차트 조회가 완료되었습니다.');
            }
        } catch (e) {
            console.error(e);
            showWarning('데이터 조회에 실패했습니다.');
        } finally {
            setLoadingSpinner(false);
        }
    };

    const handleExcelClick = async () => {
        try {
            const response = await exportEQM1030_R01Excel('', { ...EQM1030_R01Filter, baseDate: new Date() } as any, 0);
            const binaryString = atob(response.data.data);
            const byteArray = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) byteArray[i] = binaryString.charCodeAt(i);
            const blob = new Blob([byteArray]);
            downloadExcel(blob, 'EQM1030_R01.xlsx');
        } catch (e) {
            console.error(e);
            showWarning('엑셀 다운로드에 실패했습니다.');
        }
    };

    const handleInitClick = () => {
        setMessage('데이터가 초기화되었습니다.');
    };

    // 탭 변경 핸들러
    const handleTabChange = useCallback((tab: TabType) => {
        const prevTab = activeTab;
        setActiveTab(tab);
        
        // 탭 전환 시 해당 탭의 데이터가 없으면 조회
        if (prevTab !== tab) {
            if (tab === 'table' && EQM1030_R01List.length === 0) {
                setTimeout(() => fetchData(), 0);
            } else if (tab === 'chart' && chartData.length === 0) {
                setTimeout(() => fetchData(), 0);
            }
        }
    }, [setActiveTab, activeTab, EQM1030_R01List.length, chartData.length]);

    // 10분 주기 자동 조회 (모니터링)
    useEffect(() => {
        // 첫 로드 시 즉시 조회
        fetchData();
        const intervalMs = 1 * 60 * 1000; // 1분
        const id = setInterval(() => {
            // 페이지가 보이는 경우에만 조회 (백그라운드 과도한 호출 방지)
            if (document.visibilityState === 'visible') {
                fetchData();
            }
        }, intervalMs);
        return () => clearInterval(id);
        // 필터가 바뀌거나 탭이 변경되면 주기 재설정 및 즉시 재조회
    }, [EQM1030_R01Filter.compCd, activeTab]);

    // 온도( ref02 === 'C' ) 경고 메시지 생성: 설정값 대비 ±5도 이탈 시 경고
    const temperatureWarnings = useMemo(() => {
        try {
            const threshold = 5; // ±5도 기준
            const msgs: string[] = [];
            const seen = new Set<string>();
            for (const row of EQM1030_R01List) {
                const isTemp = (row as any)?.ref02 === 'C';
                if (!isTemp) continue;
                const name = (row.itemGroupName || '').toString();
                const s = typeof row.settingValue === 'string' ? parseFloat(row.settingValue) : (row.settingValue as any as number);
                const c = typeof row.currentValue === 'string' ? parseFloat(row.currentValue) : (row.currentValue as any as number);
                if (s == null || c == null || Number.isNaN(s) || Number.isNaN(c)) continue;
                const upper = s + threshold;
                const lower = s - threshold;
                if (c > upper) {
                    const m = `${name} 상한 초과`;
                    if (!seen.has(m)) { seen.add(m); msgs.push(m); }
                } else if (c < lower) {
                    const m = `${name} 하한 이탈`;
                    if (!seen.has(m)) { seen.add(m); msgs.push(m); }
                }
            }
            return msgs;
        } catch {
            return [];
        }
    }, [EQM1030_R01List]);

    return (
        <div className="content-container">
            <Actionbar
                onFetchClick={fetchData}
                onSaveClick={undefined}
                onDeleteClick={() => {}}
                onInitClick={initData}
                onExcelClick={handleExcelClick}
                onPrintClick={() => {}}
            />

            <SearchSection_EQM1030_R01 />

            {/* 탭 버튼 섹션 */}
            <div className="flex items-end mb-[0.1px] bg-white h-[25px] px-[12px]">
                <div className="flex gap-0" role="tablist" aria-label="조회 탭">
                    <button
                        onClick={() => handleTabChange('table')}
                        role="tab"
                        aria-selected={activeTab === 'table'}
                        className={`h-[26px] px-3 text-[12px] font-medium transition-colors border border-gray-300 border-b-0
                            ${activeTab === 'table'
                                ? 'bg-white text-black-700 -mb-px shadow-sm'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                        `}
                    >
                        모니터링
                    </button>
                    <button
                        onClick={() => handleTabChange('chart')}
                        role="tab"
                        aria-selected={activeTab === 'chart'}
                        className={`h-[26px] px-3 text-[12px] font-medium transition-colors border border-gray-300 border-b-0
                            ${activeTab === 'chart'
                                ? 'bg-white text-black-700 -mb-px shadow-sm'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                        `}
                    >
                        당일차트
                    </button>
                </div>
                {/* 탭 우측 경고 메시지 표시 영역 */}
                {activeTab === 'table' && temperatureWarnings.length > 0 && (
                    <div
                        className="ml-3 flex-1 overflow-hidden"
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        <div className="whitespace-nowrap overflow-x-auto px-3 py-[1px] ">
                            <span className="text-red-600 font-bold text-[16px]">
                                {temperatureWarnings.join(' / ')}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex flex-1 p-[12px] pt-[0px] gap-[4px] min-h-0">
                <div className="h-full w-full">
                    {activeTab === 'table' ? (
                        /* 매트릭스 뷰 */
                        <Table_EQM1030_R01 />
                    ) : (
                        /* 차트 뷰 */
                        <Chart_EQM1030_R01 data={chartData} />
                    )}
                </div>
            </div>

            <Message />
        </div>
    );
};


